(() => {
if (customElements.get('collection-variant-pagination')) return;

/* Shopify paginates parent products. Build customer-facing pages from their variant cards. */
class CollectionVariantPagination extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const controller = this.controller;
    this.fetchController = new AbortController();
    // Wait until an AJAX section replacement has attached all its children.
    Promise.resolve().then(() => {
      if (this.isConnected && !controller.signal.aborted) return this.initialize();
    }).catch((error) => {
      if (controller.signal.aborted || this.controller !== controller) return;
      this.fetchController.abort();
      // Keep local variant pages usable if another product page cannot be loaded.
      this.grid?.removeAttribute('aria-busy');
      if (this.isConnected && this.grid) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'load-more-btn';
        retry.textContent = 'Prøv igen';
        retry.addEventListener('click', () => window.location.reload());
        const notice = document.createElement('div');
        notice.dataset.paginationRetry = '';
        notice.setAttribute('role', 'status');
        const message = document.createElement('p');
        // message.textContent = 'Alle produkter kunne ikke indlæses.';
        // notice.append(message, retry);
        this.querySelector('[data-pagination-retry]')?.remove();
        this.append(notice);
      }
      if (error.name !== 'AbortError') console.error('Unable to paginate collection variants', error);
    });
  }

  disconnectedCallback() {
    this.controller.abort();
    this.fetchController.abort();
  }

  async initialize() {
    const section = this.closest('.shopify-section');
    this.grid = section?.querySelector('product-list');
    if (!this.grid) return;
    const sourcePage = Number(this.dataset.sourcePage);
    const sourcePages = Number(this.dataset.sourcePages);
    if (!Number.isInteger(sourcePages) || sourcePages < 1 || sourcePage < 1 || sourcePage > sourcePages) return;
    const pages = new Array(sourcePages);
    const getCards = (grid) => Array.from(grid.querySelectorAll(':scope > product-card'));
    pages[sourcePage - 1] = getCards(this.grid);
    this.baseUrl = new URL(window.location.href);
    this.baseUrl.searchParams.delete('section_id');
    this.baseUrl.searchParams.delete('variant_page');
    this.baseUrl.searchParams.delete(this.dataset.pageParam);

    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-variant-page]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.showPage(Number(link.dataset.variantPage), true);
    }, { signal: this.controller.signal });
    window.addEventListener('popstate', () => {
      // Reload to restore filters as well as the variant page from browser history.
      window.location.reload();
    }, { signal: this.controller.signal });
    // Apply the cap immediately, including after filters replace the section.
    // Keep every card in memory so later pages remain accessible.
    this.cards = pages[sourcePage - 1];
    this.pageCount = Math.max(1, Math.ceil(this.cards.length / 16));
    this.showPage(1, false);
    this.grid.setAttribute('aria-busy', 'true');

    // Fetch with the existing filters and sort order; limit concurrent requests.
    const pending = Array.from({ length: sourcePages }, (_, i) => i + 1)
      .filter((page) => page !== sourcePage);
    const worker = async () => {
      while (pending.length) {
        const page = pending.shift();
        const url = new URL(this.baseUrl);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set(this.dataset.pageParam, page);
        const response = await fetch(url, { signal: this.fetchController.signal });
        if (!response.ok) throw new Error(`Collection page ${page}: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fetchedSection = doc.getElementById(section.id);
        const grid = fetchedSection?.querySelector('product-list');
        const pager = fetchedSection?.querySelector('collection-variant-pagination');
        if (!grid || Number(pager?.dataset.sourcePage) !== page) {
          throw new Error(`Invalid collection page ${page}`);
        }
        pages[page - 1] = getCards(grid);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, pending.length) }, worker));
    if (!this.isConnected || this.controller.signal.aborted) return;
    this.cards = pages.flat();
    this.pageCount = Math.max(1, Math.ceil(this.cards.length / 16));
    const requestedPage = Number(new URL(window.location.href).searchParams.get('variant_page'));
    const initialPage = Number.isInteger(requestedPage) && requestedPage > 0
      ? requestedPage
      : Math.floor(pages.slice(0, sourcePage - 1).flat().length / 16) + 1;

    this.showPage(initialPage, false);
    this.grid.removeAttribute('aria-busy');
  }

  pageUrl(page) {
    const url = new URL(this.baseUrl);
    if (page > 1) url.searchParams.set('variant_page', page);
    return url;
  }

  showPage(requestedPage, navigate) {
    const page = Math.max(1, Math.min(this.pageCount, requestedPage));
    // Detach, rather than hide, other pages so they do not occupy grid cells.
    this.grid.querySelectorAll(':scope > product-card').forEach((card) => card.remove());
    const start = (page - 1) * 16;
    this.grid.append(...this.cards.slice(start, start + 16));
    const wrapper = document.createElement('nav');
    wrapper.className = 'custom-pagination-wrapper';
    wrapper.setAttribute('aria-label', this.getAttribute('aria-label') || 'Pagination');
    const numbers = document.createElement('div');
    numbers.className = 'pagination-numbers';
    const makeLink = (target, label, className) => {
      const link = document.createElement('a');
      link.href = this.pageUrl(target).href;
      link.dataset.variantPage = target;
      link.className = className;
      link.textContent = label;
      return link;
    };
    if (page > 1) numbers.append(makeLink(page - 1, '←', 'p-num'));
    const visiblePages = new Set([1, this.pageCount, page - 1, page, page + 1]);
    let previous = 0;
    [...visiblePages].filter((n) => n >= 1 && n <= this.pageCount).sort((a, b) => a - b).forEach((n) => {
      if (previous && n - previous > 1) {
        const dots = document.createElement('span');
        dots.className = 'p-dots';
        dots.textContent = '…';
        numbers.append(dots);
      }
      const link = makeLink(n, String(n), `p-num${n === page ? ' active' : ''}`);
      if (n === page) link.setAttribute('aria-current', 'page');
      numbers.append(link);
      previous = n;
    });
    if (this.pageCount > 1) {
      wrapper.append(numbers);
      if (page < this.pageCount) {
        wrapper.append(makeLink(page + 1, 'ÅBNE MERE', 'load-more-btn'));
      }
    }
    const count = document.createElement('div');
    count.className = 'count-text';
    count.setAttribute('role', 'status');
    count.textContent = `se ${Math.min(start + 16, this.cards.length)} produkter ud af ${this.cards.length}`;
    wrapper.append(count);
    // Preserve the shared pagination stylesheet emitted by the Liquid snippet.
    this.querySelectorAll(':scope > :not(style):not([data-pagination-retry])').forEach((child) => child.remove());
    this.append(wrapper);
    if (navigate) {
      history.pushState({}, '', this.pageUrl(page));
      this.grid.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }
}

if (!customElements.get('collection-variant-pagination')) {
  customElements.define('collection-variant-pagination', CollectionVariantPagination);
}

// A new filter or sort starts at the first page of its new variant results.
document.addEventListener('facet:update', (event) => {
  const url = event.detail?.url;
  if (!(url instanceof URL)) return;
  const section = document.getElementById(`shopify-section-${url.searchParams.get('section_id')}`);
  if (section?.querySelector('collection-variant-pagination')) {
    url.searchParams.delete('variant_page');
  }
}, true);

})();
